import {useRef,useState,type CSSProperties,type ReactNode} from 'react';
import {reasoningColor} from './composer-model';

const seed=(index:number,salt:number)=>{const raw=Math.sin((index+1)*(salt+11)*12.9898)*43758.5453;return raw-Math.floor(raw);};
// Keep the drift visible with translucent specks, without white cores or outlines.
const particles=Array.from({length:22},(_,index)=>({
 x:324*(index+.5+(seed(index,17)-.5)*.34)/22,
 y:58*Math.min(.88,Math.max(.12,seed(index,41))),
 radius:1.1+seed(index,73)*.8,opacity:.42+seed(index,101)*.20
}));

export function ComposerTuningPicker({kind,label,options,value,onChange,disabled=false,title,note}:{
 kind:'reasoning'|'verbosity';label:string;options:{id:string;label:string}[];value:string;
 onChange:(value:string)=>void;disabled?:boolean;title:(value:string)=>ReactNode;note?:ReactNode;
}){
 const [drag,setDrag]=useState<number|null>(null),dragging=useRef(false);
 const selected=Math.max(0,options.findIndex(item=>item.id===value)),maximum=Math.max(0,options.length-1);
 const position=disabled?selected:drag??selected,fraction=maximum?position/maximum:0;
 const effective=options[Math.round(position)]?.id??value;
 const railColor=kind==='reasoning'?reasoningColor(effective):'#000';
 const color=kind==='verbosity'||effective==='low'?`var(--reados-text, ${railColor})`:railColor;
 // Keep the discrete control stops legible on orange and mint fills.
 const brightFill=kind==='reasoning'&&['max','ultra'].includes(effective);
 const commit=(position:number)=>{
  dragging.current=false;setDrag(null);
  const next=options[Math.round(position)];
  if(!disabled&&next&&next.id!==value)onChange(next.id);
 };
 return <div className={`chat-ipad-tuning is-${kind}`} style={{color}}>
  <div className="chat-ipad-tuning-header">{title(effective)}</div>
  {options.length>0&&<div className={`chat-ipad-tuning-rail${disabled?' is-disabled':''}${drag!==null?' is-dragging':''}${brightFill?' has-dark-dots':''}`}
   style={{'--tuning-fraction':fraction,'--tuning-color':railColor} as CSSProperties}>
   <div className="chat-ipad-tuning-active" aria-hidden="true">
    <svg className="chat-ipad-tuning-particles" width="324" height="58" aria-hidden="true"><g>{[-324,0].flatMap(offset=>particles.map((particle,index)=><circle key={`${offset}:${index}`} cx={particle.x+offset} cy={particle.y} r={particle.radius} fill="white" opacity={particle.opacity}/>))}</g></svg>
   </div>
   <div className="chat-ipad-tuning-dots" aria-hidden="true">{options.map((item,index)=><i key={item.id} className={index<=position?'is-active':''} style={{left:`${maximum?index/maximum*100:0}%`}}/>)}</div>
   <div className="chat-ipad-tuning-thumb" aria-hidden="true"/>
   <input className="chat-ipad-tuning-input" type="range" aria-label={label} aria-valuetext={options[Math.round(position)]?.label??value}
    min={0} max={maximum} step="any" value={position} disabled={disabled||options.length<2}
    onPointerDown={()=>{dragging.current=true;}}
    onChange={event=>{const position=Number(event.currentTarget.value);if(dragging.current)setDrag(position);else commit(position);}}
    onPointerUp={event=>{if(dragging.current)commit(Number(event.currentTarget.value));}}
    onPointerCancel={()=>{dragging.current=false;setDrag(null);}}
    onBlur={event=>{if(dragging.current)commit(Number(event.currentTarget.value));}}
    onKeyDown={event=>{
     const next=event.key==='Home'?0:event.key==='End'?maximum:['ArrowRight','ArrowUp','PageUp'].includes(event.key)?selected+1:['ArrowLeft','ArrowDown','PageDown'].includes(event.key)?selected-1:undefined;
     if(next===undefined)return;event.preventDefault();commit(Math.max(0,Math.min(maximum,next)));
    }}/>
  </div>}
  {note}
 </div>;
}
